// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import t1AuthBand from "@/assets/photography/t1-auth-band.webp"
import t2KitchenBand from "@/assets/photography/t2-kitchen-band.webp"

/**
 * ADR-070 CATEGORY B — atmosphere, with its provenance.
 *
 * The web twin of `mobile/src/ui/atmosphere.ts`, and the sibling of mobile's
 * `ui/photos.ts`, deliberately the same shape. That one is
 * category A — an image asserting *"this is this BiteBalance meal / this
 * ingredient / your plan"* — and it stays EMPTY, because no verified source
 * exists. This one is category B: atmosphere that claims nothing about a
 * specific recipe, ingredient or plan.
 *
 * The registry is keyed by PLACEMENT, not by asset, and that is the whole
 * safety mechanism. A component asks for the asset approved for the surface it
 * is rendering; there is no way to ask for "an atmospheric image" in general,
 * so "atmospheric" can never become a default a future contributor invokes
 * (ADR-070 rule 5). A placement that is not in this map has no asset, and the
 * surfaces where proximity would read as depiction — meal cards, recipe heroes,
 * ingredient rows, plan slots, a populated Kitchen — are simply not placements.
 *
 * Assets approved by the founder on 2026-09-11. Nothing here changes ADR-070.
 */


/** The only surfaces an atmospheric photograph may appear on. */
export type AtmospherePlacement =
  /** The unauthenticated threshold: the sign-in / sign-up dialog. No plan data exists yet. */
  | "auth_threshold"
  /** Kitchen's opt-in and empty states ONLY — never above real inventory (ADR-070 rule 1). */
  | "kitchen_band"

export interface AtmosphereAsset {
  /** Where the frame came from. */
  source: string
  photographer: string
  /** The library's own identifier, so the licence can be re-checked at any time. */
  assetId: string
  licence: string
  /**
   * Why we are allowed to ship it — recorded rather than assumed. Verified on
   * the asset's own page, not inferred from a download button: the design-review
   * comp for this direction was Unsplash+, a paid tier, and is NOT this asset.
   */
  basis: string
  placement: AtmospherePlacement
  /** ADR-070 category. Only "B" may ever appear in this file. */
  category: "B"
  /**
   * Decorative: the photograph is atmosphere behind or beneath type, and the
   * type is the content. It is hidden from assistive technology rather than
   * described, because describing it would invite a food claim (rule 4). The
   * scene descriptions below exist for the record and for a future non-decorative
   * placement — they name a scene, never a food.
   */
  decorative: true
  sceneEn: string
  sceneDe: string
  /**
   * Vertical anchor for a cover-crop, 0 = top, 1 = bottom. Declared per asset
   * because a centred crop can land on a frame's least informative slice.
   */
  focalY: number
  /** The imported URL of the derivative, ready for `background-image` / `<img src>`. */
  asset: string
}

const REGISTRY: Record<AtmospherePlacement, AtmosphereAsset> = {
  auth_threshold: {
    source: "Unsplash",
    photographer: "Luke Oslizlo (@lukaszmtw)",
    assetId: "C7Bte_Pcpvk",
    licence: "Unsplash License",
    basis:
      "Free for commercial use, web and iOS, modification and cropping permitted, no attribution required; " +
      "verified on the asset's own page 2026-09-11. Restrictions (no selling unaltered copies, no compiling " +
      "Unsplash images into a competing service) do not apply to bundling inside this product.",
    placement: "auth_threshold",
    category: "B",
    decorative: true,
    sceneEn: "a kitchen in low evening light, seen through window blinds",
    sceneDe: "eine Küche im tiefen Abendlicht, durch Jalousien gesehen",
    focalY: 0.5,
    asset: t1AuthBand,
  },
  kitchen_band: {
    source: "Unsplash",
    photographer: "Annie Spratt (@anniespratt)",
    assetId: "FW6rGAoEjr4",
    licence: "Unsplash License",
    basis:
      "Free for commercial use, web and iOS, modification and cropping permitted, no attribution required; " +
      "verified on the asset's own page 2026-09-11.",
    placement: "kitchen_band",
    category: "B",
    decorative: true,
    sceneEn: "a kitchen counter in daylight",
    sceneDe: "eine Küchenarbeitsfläche bei Tageslicht",
    // a centred crop of this frame lands on its least informative slice and reads
    // as a pale blur at 132px; anchoring low brings the worktop and hob into the band
    focalY: 0.68,
    asset: t2KitchenBand,
  },
}

/**
 * The approved asset for a placement, or null.
 *
 * Null is a supported state everywhere this is used: every placement renders
 * correctly with no photograph — the auth dialog falls back to its plain header,
 * the Kitchen band collapses to the larder ground. A
 * photograph is never load-bearing for legibility or for meaning.
 */
export function atmosphereFor(placement: AtmospherePlacement): AtmosphereAsset | null {
  return REGISTRY[placement] ?? null
}
